import { computed, inject, Injectable } from '@angular/core';
import { ThemeService } from '../services/theme.service';
import { ECHARTS_V5_LEGACY_THEME } from './echarts-v5-legacy-theme';

/**
 * ECharts theme objects for the Wheere palette.
 *
 * Why this exists: none of the monitoring charts passed a theme, so ECharts fell
 * back to its built-in light defaults — black axis labels and a white tooltip —
 * regardless of the app theme. On the dark shell that made every chart unreadable.
 *
 * These are plain objects (not registered theme names) because `echarts.init()`
 * accepts a theme object directly, which keeps registration order out of the
 * picture. Bind one through ngx-echarts' `[theme]` input:
 *
 *     <div echarts [options]="chartOptions" [theme]="echartsTheme()"></div>
 *
 * Scope: chart *chrome* only — axes, grid, tooltip, legend, toolbox, dataZoom.
 * Series colours stay with the callers (`chartCustomColors`), which set
 * `itemStyle`/`lineStyle` explicitly so a theme `color` array would not win anyway.
 *
 * ECharts 6 : chaque thème est posé PAR-DESSUS le thème « v5 » officiel
 * ({@link ECHARTS_V5_LEGACY_THEME}). ECharts 6 a changé ses valeurs par défaut (légende en bas,
 * marges, palette, poignées du zoom...) ; un thème passé à `echarts.init()` ne redéfinit que ce
 * qu'il nomme, donc sans ce socle tout le reste prendrait l'apparence de la v6.
 */

type ThemeObject = Record<string, unknown>;

function isPlainObject(value: unknown): value is ThemeObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Fusion profonde sans mutation : les objets se combinent, tout le reste (tableaux compris) est
 * remplacé par la valeur de `override`. */
export function mergeEchartsTheme(base: ThemeObject, override: ThemeObject): ThemeObject {
  const merged: ThemeObject = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const existing = merged[key];
    merged[key] = isPlainObject(existing) && isPlainObject(value) ? mergeEchartsTheme(existing, value) : value;
  }
  return merged;
}

/** `backgroundColor: 'transparent'` lets the card underneath supply the surface,
 * so one theme works on white, slate-800, or a dialog. */
const BASE = {
  backgroundColor: 'transparent',
  textStyle: { fontFamily: "'Lato', ui-sans-serif, system-ui, sans-serif" },
};

function axis(labelColor: string, lineColor: string, splitColor: string) {
  return {
    axisLine: { show: true, lineStyle: { color: lineColor } },
    axisTick: { show: true, lineStyle: { color: lineColor } },
    axisLabel: { show: true, color: labelColor },
    splitLine: { show: true, lineStyle: { color: [splitColor] } },
    splitArea: { show: false },
    nameTextStyle: { color: labelColor },
  };
}

function build(c: {
  text: string;
  muted: string;
  line: string;
  split: string;
  /** The card surface behind the chart — used to fill the dataZoom handles. */
  surface: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  zoomFill: string;
  zoomHandle: string;
}) {
  const axes = axis(c.muted, c.line, c.split);
  return mergeEchartsTheme(ECHARTS_V5_LEGACY_THEME, {
    ...BASE,
    textStyle: { ...BASE.textStyle, color: c.text },
    title: { textStyle: { color: c.text }, subtextStyle: { color: c.muted } },
    legend: { textStyle: { color: c.muted }, inactiveColor: c.split, pageTextStyle: { color: c.muted } },
    tooltip: {
      backgroundColor: c.tooltipBg,
      borderColor: c.tooltipBorder,
      borderWidth: 1,
      textStyle: { color: c.tooltipText },
      axisPointer: { lineStyle: { color: c.muted }, crossStyle: { color: c.muted } },
    },
    categoryAxis: axes,
    valueAxis: axes,
    logAxis: axes,
    timeAxis: axes,
    toolbox: {
      iconStyle: { borderColor: c.muted },
      emphasis: { iconStyle: { borderColor: c.text } },
    },
    // The filler spans the whole slider, so it stays neutral and translucent —
    // only the handles carry the brand accent. Tinting the filler (or the move
    // handle) primary paints a solid pink slab across the axis.
    dataZoom: {
      backgroundColor: 'transparent',
      borderColor: c.line,
      fillerColor: c.zoomFill,
      handleStyle: { color: c.surface, borderColor: c.zoomHandle, borderWidth: 1.5 },
      moveHandleStyle: { color: c.line, opacity: 0.6 },
      emphasis: { moveHandleStyle: { color: c.muted } },
      textStyle: { color: c.muted },
      dataBackground: { lineStyle: { color: c.line, opacity: 0.6 }, areaStyle: { color: c.split, opacity: 0.6 } },
      selectedDataBackground: {
        lineStyle: { color: c.zoomHandle, opacity: 0.8 },
        areaStyle: { color: c.zoomHandle, opacity: 0.15 },
      },
    },
    markLine: { label: { color: c.text } },
    markPoint: { label: { color: c.text } },
  });
}

/** gray-900 text on gray-500 axes — matches the light card surface (`bg-white`). */
export const WH_ECHARTS_LIGHT = build({
  text: '#111827',
  muted: '#6B7280',
  line: '#D1D5DB',
  split: '#F3F4F6',
  surface: '#FFFFFF',
  tooltipBg: '#FFFFFF',
  tooltipBorder: '#E5E7EB',
  tooltipText: '#111827',
  zoomFill: 'rgba(107, 114, 128, 0.15)',
  zoomHandle: '#ED107D',
});

/** slate-100 text on slate-400 axes — matches the dark card surface (`bg-slate-800`). */
export const WH_ECHARTS_DARK = build({
  text: '#F1F5F9',
  muted: '#94A3B8',
  line: '#475569',
  split: '#334155',
  surface: '#1E293B',
  tooltipBg: '#1E293B',
  tooltipBorder: '#475569',
  tooltipText: '#F1F5F9',
  zoomFill: 'rgba(148, 163, 184, 0.18)',
  zoomHandle: '#ED107D',
});

/**
 * Resolves the ECharts theme from the app theme. Inject and bind:
 *
 *     protected readonly echartsTheme = inject(WhEchartsThemeService).theme;
 *
 * ngx-echarts disposes and re-creates the chart when this changes, so a
 * component holding an ECharts instance must re-apply its data in
 * `(chartInit)` — the instance it captured before the switch is dead.
 */
@Injectable({ providedIn: 'root' })
export class WhEchartsThemeService {
  #theme = inject(ThemeService);
  readonly theme = computed(() => (this.#theme.resolved() === 'dark' ? WH_ECHARTS_DARK : WH_ECHARTS_LIGHT));
}
